import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, ShieldCheck, Tag, RotateCcw, Undo2 } from "lucide-react";
import api from "../lib/api";

const TYPES = {
  referral_bonus: { label: "Bonus invito", Icon: Gift, cls: "bg-emerald-100 text-emerald-800" },
  report_bonus: { label: "Bonus segnalazione", Icon: ShieldCheck, cls: "bg-sky-100 text-sky-800" },
  order_discount: { label: "Sconto su ordine", Icon: Tag, cls: "bg-slate-200 text-slate-800" },
  credit_released: { label: "Credito rimosso dall'ordine", Icon: Undo2, cls: "bg-amber-100 text-amber-800" },
  dispute_refund: { label: "Rimborso contestazione", Icon: RotateCcw, cls: "bg-emerald-100 text-emerald-800" },
};
const eur = (n) => `€ ${Math.abs(Number(n || 0)).toFixed(2)}`;

export default function CreditHistory() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/me/credit/ledger").then(({ data }) => setData(data)).catch(() => setData({ balance: 0, entries: [] })); }, []);
  const entries = data?.entries || [];
  const earned = entries.filter(e => e.amount > 0).reduce((a, e) => a + e.amount, 0);
  const spent = entries.filter(e => e.amount < 0).reduce((a, e) => a - e.amount, 0);
  return (
    <div data-testid="credit-panel">
      <div className="ga-card p-5 flex flex-wrap items-center gap-6 mb-5">
        <Gift className="w-5 h-5 text-emerald-700" />
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Credito disponibile</div>
          <div className="font-display text-3xl font-extrabold text-slate-900" data-testid="credit-balance">€ {Number(data?.balance || 0).toFixed(2)}</div>
        </div>
        <div className="text-sm text-slate-600">Accumulato <b className="font-mono">{eur(earned)}</b> · Speso <b className="font-mono">{eur(spent)}</b></div>
        <div className="text-xs text-slate-500 basis-full">Il credito si usa come sconto al momento del pagamento di un ordine (spunta "Usa il tuo credito"). Non è prelevabile.</div>
      </div>
      {data === null && <div className="text-sm text-slate-500 py-6">Caricamento...</div>}
      {data && entries.length === 0 && <div className="ga-card p-8 text-center text-sm text-slate-500" data-testid="credit-empty">Nessun movimento di credito finora. Invita un collega o segnala una proposta fuori piattaforma per guadagnare credito.</div>}
      <div className="space-y-2">
        {entries.map(e => {
          const t = TYPES[e.type] || { label: e.type, Icon: Tag, cls: "bg-slate-100 text-slate-700" };
          const inner = (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex items-center gap-3">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${t.cls}`}><t.Icon className="w-3 h-3" /> {t.label}</span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{e.order_title || e.note || "—"}</div>
                  <div className="text-xs font-mono text-slate-500">{new Date(e.created_at).toLocaleString("it-IT")}{e.order_title && e.note ? ` · ${e.note}` : ""}</div>
                </div>
              </div>
              <div className={`font-mono font-bold ${e.amount >= 0 ? "text-emerald-700" : "text-slate-900"}`} data-testid={`credit-entry-amount-${e.id}`}>{e.amount >= 0 ? "+" : "−"}{eur(e.amount)}</div>
            </div>
          );
          return e.order_id
            ? <Link key={e.id} to={`/ordine/${e.order_id}`} className="ga-card p-4 block hover:border-slate-400" data-testid={`credit-entry-${e.id}`}>{inner}</Link>
            : <div key={e.id} className="ga-card p-4" data-testid={`credit-entry-${e.id}`}>{inner}</div>;
        })}
      </div>
    </div>
  );
}
